import { AxiosError } from 'axios'

import { InternalError } from './elements/InternalError'
import { SingleError } from './elements/SingleError/SingleError'
import { StructuredError as StructuredErrorComponent } from './elements/StructuredError/StructuredError'
import { ErrorItem } from './elements/types'
import { ValidationErrors } from './elements/ValidationErrors/ValidationErrors'

type FormData = {
  channel?: string
  placement?: string
  fileName?: string
}

type ErrorViewProps = {
  error: AxiosError<unknown> | null
  onBack: () => void
  formData?: FormData
}

type StructuredError = {
  message?: string
  missing_headers?: string[]
  found_headers?: string[]
  code?: string
  errors?: ErrorItem[]
  details?: string
  [key: string]: unknown
}

const ErrorView = ({ error, onBack }: ErrorViewProps) => {
  const getErrorData = (): StructuredError | string | null => {
    if (!error) return null

    const errorData = error.response?.data || error.message

    if (typeof errorData === 'object' && errorData !== null) {
      return errorData as StructuredError
    }

    return String(errorData)
  }

  const errorData = getErrorData()

  // Check if error is INTERNAL_ERROR
  const isInternalError =
    errorData &&
    typeof errorData === 'object' &&
    'code' in errorData &&
    (errorData as StructuredError).code === 'INTERNAL_ERROR'

  if (isInternalError) {
    const internalError = errorData as StructuredError
    return (
      <InternalError
        message={internalError.message}
        details={internalError.details}
        onBack={onBack}
      />
    )
  }

  // Check if error has headers (MISSING_HEADERS or structured error with missing_headers/found_headers)
  const isMissingHeaders =
    errorData &&
    typeof errorData === 'object' &&
    'code' in errorData &&
    (errorData as StructuredError).code === 'MISSING_HEADERS'

  const isStructuredError =
    errorData &&
    typeof errorData === 'object' &&
    'message' in errorData &&
    ('missing_headers' in errorData || 'found_headers' in errorData)

  const structuredError =
    isMissingHeaders || isStructuredError
      ? (errorData as StructuredError)
      : null

  // Check if error is VALIDATION_ERRORS (contains array of errors)
  const isValidationErrors =
    errorData &&
    typeof errorData === 'object' &&
    'code' in errorData &&
    (errorData as StructuredError).code === 'VALIDATION_ERRORS' &&
    'errors' in errorData &&
    Array.isArray((errorData as StructuredError).errors)

  if (isValidationErrors) {
    const validationErrors = errorData as StructuredError & {
      errors: ErrorItem[]
    }

    return <ValidationErrors errors={validationErrors.errors} onBack={onBack} />
  }

  // Check if error has a code field (single error object)
  // Only check this if it's not a structured error or validation errors
  const hasErrorCode =
    !isStructuredError &&
    !isValidationErrors &&
    errorData &&
    typeof errorData === 'object' &&
    'code' in errorData &&
    typeof (errorData as StructuredError).code === 'string'

  const errorItem = hasErrorCode ? (errorData as ErrorItem) : null

  // Render single error item if available
  if (errorItem) {
    return <SingleError error={errorItem} onBack={onBack} />
  }

  if (structuredError) {
    return (
      <StructuredErrorComponent
        code={structuredError.code}
        message={structuredError.message}
        missing_headers={structuredError.missing_headers}
        found_headers={structuredError.found_headers}
        onBack={onBack}
      />
    )
  }

  // Fallback to InternalError for unknown errors
  const getErrorDetails = () => {
    if (!errorData) return undefined

    if (typeof errorData === 'string') {
      return errorData
    }

    return JSON.stringify(errorData, null, 2)
  }

  return (
    <InternalError
      message={
        typeof errorData === 'object' &&
        errorData !== null &&
        'message' in errorData
          ? String((errorData as StructuredError).message)
          : undefined
      }
      details={getErrorDetails()}
      onBack={onBack}
    />
  )
}

export default ErrorView
